import { themes } from '@/app/themes'

// Base color arrays (used by inline script and padded for runtime)
export const baseColors = {
    og: ['#f56476', '#e43f6f', '#be3e82', '#D6CA98', '#dfbbb1'],
    purpleToPink: ['#C667CF', '#C253A9', '#be3e82', '#d13f79', '#e43f6f', '#ed5273', '#f56476'],
    purplish: ['#655a7c', '#ab92bf', '#afc1d6', '#cef9f2', '#d6ca98'],
    earthy2: ['#7ea779', '#809f77', '#839073', '#797a6b', '#6e6362', '#5e575e', '#4e4a59', '#394053'],
    blues: ['#a8e0ff', '#8ee3f5', '#70cad1', '#3e517a', '#b08ea2'],
    bw: ['#CCCCCC', '#999999', '#666666', '#333333', '#000000'],
    coffee: ['#321325', '#5f0f40', '#7d092f', '#9a031e', '#b33e2c', '#cb793a', '#e4ab44', '#fcdc4d'],
    business: ['#c99da3', '#c9adb7', '#c8bdca', '#c6ddf0', '#996888', '#7c596f', '#5e4955', '#2a2b2a'],
    clear: ['rgba(255,101,150,.7)', 'rgba(255,101,150,.5)', 'rgba(255,101,150,.3)', 'rgba(255,101,150,.1)'],
    yaleBlue: ['#134074', '#13315c', '#0b2545', '#2c4665', '#4c6785', '#8da9c4', '#becfd9', '#eef4ed'],
    pinkLavender: ['#d1b1c8', '#c1b4cd', '#b1b7d1', '#9b9fb5', '#94899d', '#8c7284', '#7a6174'],
    bumblebee: ['#1c110a', '#fed801'],
    emerald: ['#304035', '#344b3b', '#3b5c45', '#416c4f', '#56a170', '#5eb77d', '#66cc8a', '#81d59f'],
    halloween: ['#ff8f00', '#ef7b21', '#df6741', '#a84e31', '#703421', '#542719'],
    sunset: ['#ff0000', '#f73e11', '#f54e15', '#f35d19', '#ef7b21', '#f38f1a', '#f7a213', '#ffc905'],
    // Additional colors not used in theme mapping but available
    dark: ['#000000', '#151515', '#292929', '#3e3e3e', '#525252', '#7B7B7B', '#a4a4a4'],
    greys: ['#a4a4a4', '#acacac', '#909090', '#7b7b7b', '#676767', '#525252', '#3e3e3e', '#343434', '#292929'],
    earthy: ['#d4e09b', '#f6f4d2', '#cbdfbd', '#f19c79', '#a44a3f'],
    something: ['#64113f', '#de4d86', '#f29ca3', '#f7cacd', '#84e6f8'],
    jet: ['#353535', '#9a9a9a', '#ffffff', '#e9ebef', '#d2d7df', '#c8c9c8', '#bdbbb0', '#a4a296', '#8a897c'],
    spaceCadet: ['#2d3047', '#607483', '#93b7be', '#bac17d', '#e0ca3c', '#c4b27a', '#a799b7', '#56929c', '#048a81'],
    wingIt: ['#ff6596', '#ff7e71', '#ff974b', '#ffb026', '#ffc800', '#ff700c', '#ff4412', '#ff2e15'],
    timberWolf: ['#dad7cd', '#bfc4ac', '#a3b18a', '#7e9971', '#588157', '#496e4c', '#3a5a40', '#344e41'],
    copilot: ['#f5f3f7', '#e8e5eb', '#dcd9e0', '#d0cdd6', '#c4c1cc', '#b8b5c2', '#acabb9', '#a09fa9'],
    mindaro: ['#ecfeaa', '#e7ee97', '#e2de84', '#c4c27a', '#a6a670', '#86836d', '#626267'],
} as const

// Theme to colorId mapping (used by inline script and colorIdFromTheme)
export const themeToColorId: Record<ThemeId, keyof typeof baseColors> = {
    black: 'bw',
    lofi: 'bw',
    cyberpunk: 'clear',
    night: 'blues',
    business: 'business',
    cupcake: 'blues',
    autumn: 'coffee',
    coffee: 'coffee',
    halloween: 'halloween',
    bumblebee: 'bumblebee',
    forest: 'earthy2',
    dim: 'earthy2',
    emerald: 'emerald',
    winter: 'yaleBlue',
    aqua: 'yaleBlue',
    nord: 'purplish',
    pastel: 'pinkLavender',
    dracula: 'purpleToPink',
    synthwave: 'purpleToPink',
    valentine: 'purpleToPink',
    sunset: 'sunset',
}

const pad = (arr: readonly string[]) => [...arr, ...arr, ...arr, ...arr, ...arr]

// Padded colors for runtime use
export const langColors = Object.fromEntries(Object.entries(baseColors).map(([key, colors]) => [key, pad(colors)])) as {
    [K in keyof typeof baseColors]: string[]
}

export type ColorIds = keyof typeof langColors
export type ThemeId = (typeof themes)[number]

export function colorIdFromTheme(theme: ThemeId): ColorIds {
    return themeToColorId[theme] || 'og'
}
